import { shortAddress, timeConverter } from '../../../utils';
import { useT } from '../../../i18n';
import { useState, React, useEffect } from 'react';
import lbDialog from '../Modals/lbDialog';
import Tooltip from '../Tooltip/onButton';
import { Info, Trophy, XCircle } from 'lucide-react';
import bitcoin from '../../../assets/utility/bitcoin.png';
import ethereum from '../../../assets/utility/ethereum.png';
import matic from '../../../assets/utility/matic.png';
import CRANNI from '../../../assets/utility/CRANNI.png';
import luckblocksLogo from '../../../assets/images/luckblocks.png';
import {
  luckBlocksAddress,
  ethLuckBlocksAddress,
  maticLuckBlocksAddress,
  CRANNILuckBlocksAddress,
} from '../../../.config';
import LuckBlocks from '../../../abi/LuckBlocks.json';
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { useAppKitAccount } from "@reown/appkit/react";
import { ethers } from 'ethers';

const COINS = {
  Bitcoin: { icon: bitcoin, symbol: 'BTC' },
  Ethereum: { icon: ethereum, symbol: 'ETH' },
  Matic: { icon: matic, symbol: 'POL' },
  CRANNI: { icon: CRANNI, symbol: 'CRANNI' },
};

const TicketItem = ({ event, resetTickets }) => {
  const t = useT();
  const { address, isConnected } = useAppKitAccount();
  const [clicked, setClicked] = useState(false);
  const [resetted, setResetted] = useState(false);

  const {
    writeContract,
    data: hash,
    error: writeError,
    isPending: isWritePending
  } = useWriteContract();

  const {
    isLoading: isConfirming,
    isSuccess: isConfirmed,
    error: receiptError
  } = useWaitForTransactionReceipt({
    hash,
  });

  // Handle transaction success/failure
  useEffect(() => {
    if (isConfirmed && hash) {
      console.log('Transaction successful!');
      lbDialog.fire({
        title: t('tx.success'),
        text: t('tx.successHash') + hash,
        icon: 'success',
      });
      setTimeout(() => {
        resetTickets();
      }, 3000);
      setClicked(false);
    }
  }, [isConfirmed, hash, resetTickets]);

  // Handle write errors
  useEffect(() => {
    if (writeError) {
      console.error('Write error:', writeError);
      lbDialog.fire({
        title: t('tx.error'),
        text: t('tx.failed') + (writeError.message || t('common.unknownError')),
        icon: 'error',
        showConfirmButton: false,
        timer: 3000,
      });
      setClicked(false);
    }
  }, [writeError]);

  // Handle receipt errors
  useEffect(() => {
    if (receiptError) {
      console.error('Receipt error:', receiptError);
      lbDialog.fire({
        title: t('tx.error'),
        text: t('tx.confirmFailed') + (receiptError.message || t('common.unknownError')),
        icon: 'error',
        showConfirmButton: false,
        timer: 3000,
      });
      setClicked(false);
    }
  }, [receiptError]);

  async function forfeitTicket(lottery, id) {
    if (!isConnected || !address) {
      lbDialog.fire({
        title: t('tx.warning'),
        text: t('tx.connectFirst'),
        icon: 'warning',
      });
      return;
    }

    setClicked(true);
    let contractAddress;

    if (lottery === 'Bitcoin') {
      contractAddress = luckBlocksAddress;
    } else if (lottery === 'Ethereum') {
      contractAddress = ethLuckBlocksAddress;
    } else if (lottery === 'Matic') {
      contractAddress = maticLuckBlocksAddress;
    } else if (lottery === 'CRANNI') {
      contractAddress = CRANNILuckBlocksAddress;
    }

    try {
      writeContract({
        address: contractAddress,
        abi: LuckBlocks.abi,
        functionName: 'ForfeitTicket',
        args: [id],
      });
    } catch (error) {
      console.error('Error writing contract:', error);
      setClicked(false);
    }
  }

  if (!event) return null;

  const coin = COINS[event.lottery] || COINS.CRANNI;
  const isSettled = event.weekly === 3;
  const canForfeit = event.weekly === 0;
  const isBusy = isWritePending || isConfirming;

  return (
    <article className="lb-hoverable mx-auto flex h-full w-full max-w-[290px] flex-col overflow-hidden rounded-[26px] border border-amber-400/30 bg-gradient-to-b from-[#15100227] via-[#0c0910] to-black p-5 text-white">
      {/* Ticket header */}
      <header className="flex items-center justify-between gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/5">
          <img src={coin.icon} alt="" aria-hidden="true" className="h-6 w-6" />
        </span>
        <div className="text-right">
          <p className="lb-label">{t('item.ticket')}</p>
          <p className="notranslate lb-num text-lg font-bold leading-tight">#{event.ticketId}</p>
        </div>
      </header>

      <div className="my-4 flex items-center justify-center rounded-2xl border border-white/5 bg-black/50 px-4 py-3">
        <img src={luckblocksLogo} alt="Luckblocks" className="w-full max-w-[180px]" />
      </div>

      {/* Perforation */}
      <div className="relative my-1 flex items-center" aria-hidden="true">
        <span className="h-3 w-3 -translate-x-[22px] rounded-full bg-black" />
        <span className="flex-1 border-t border-dashed border-white/20" />
        <span className="h-3 w-3 translate-x-[22px] rounded-full bg-black" />
      </div>

      <dl className="mt-3 flex-1 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <dt className="text-[0.65rem] uppercase tracking-[0.16em] text-white/50">{t('item.drawId')}</dt>
          <dd className="notranslate lb-num text-sm font-bold">{event.drawId}</dd>
        </div>

        {!isSettled ? (
          <>
            {/* Stacked, not side-by-side: the card is narrow in a 4-up carousel and
                a full timestamp wraps badly next to its label. */}
            <div>
              <dt className="text-[0.65rem] uppercase tracking-[0.16em] text-white/50">
                {t('item.drawingAt')}
              </dt>
              <dd className="notranslate lb-num mt-1 text-sm font-semibold leading-snug">
                {timeConverter(event.nextSpin / 1000)}
              </dd>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-3 text-center">
              <dt className="text-[0.65rem] uppercase tracking-[0.16em] text-white/50">
                {t('item.onJackpotWin')}
              </dt>
              <dd className="notranslate lb-num mt-1.5 font-display text-2xl leading-none text-white">
                {event.jackpotWinC} {coin.symbol}
              </dd>
              <dd className="notranslate lb-num mt-1 text-xs text-white/50">
                ≈ {event.jackpotWinU} USD
              </dd>
            </div>
          </>
        ) : (
          <div
            className={`flex items-center justify-center gap-2 rounded-2xl border px-3 py-3 text-sm font-bold uppercase tracking-[0.14em] ${
              event.winner === true
                ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300'
                : 'border-white/10 bg-white/[0.03] text-white/50'
            }`}
          >
            {event.winner === true ? (
              <>
                <Trophy className="h-4 w-4" aria-hidden="true" /> {t('item.winner')}
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4" aria-hidden="true" /> {t('item.noWin')}
              </>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-white/5 pt-3">
          <dt className="text-[0.65rem] uppercase tracking-[0.16em] text-white/50">{t('item.owner')}</dt>
          <dd className="notranslate text-xs font-semibold">{shortAddress(event.owner)}</dd>
        </div>
      </dl>

      {canForfeit && (
        <Tooltip
          message={t('item.forfeitTooltip')}
          className="mt-5 w-full"
        >
          <button
            onClick={() => forfeitTicket(event.lottery, event.ticketId)}
            disabled={isBusy}
            className={`lb-btn lb-btn-gold lb-btn-sm w-full ${clicked ? 'clicked' : ''}`}
          >
            {isWritePending ? t('common.confirming') : isConfirming ? t('common.processing') : t('item.forfeit')}
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </Tooltip>
      )}
    </article>
  );
};

export default TicketItem;
